/**
 * Header Functionality
 * Handles search typeahead, customer dropdown, and navigation dropdowns
 */

(function() {
  'use strict';
  
  // Get configuration from global object or data attributes
  const config = window.SHOPIFY_CONFIG || {};
  const moneyFormat = config.moneyFormat || '{{amount}}';
  const translations = config.translations || {};
  
  /**
   * Escape HTML to prevent XSS attacks
   * @param {string} text - The text to escape
   * @returns {string} - The escaped HTML-safe string
   */
  const escapeHtml = function(text) {
    if (text === null || text === undefined) {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  };
  
  // Search Typeahead Functionality
  const initSearchTypeahead = function() {
    const searchInputs = document.querySelectorAll('#header-search-input, #mobile-search-input');
    
    // Get translations from config or use defaults
    const collectionsLabel = translations.collectionsLabel || 'Collections';
    const productsLabel = translations.productsLabel || 'Products';
    const searchingLabel = translations.searchingLabel || 'Searching...';
    const searchUnavailableLabel = translations.searchUnavailableLabel || 'Search temporarily unavailable';
    const noResultsLabel = translations.noResultsLabel || 'No results found';
    const collectionTypeLabel = translations.collectionTypeLabel || 'Collection';
    const fromPriceLabel = translations.fromPriceLabel || 'From';
    
    // Search cache configuration
    const MAX_CACHE_SIZE = 50;
    const searchCacheKeys = []; // Track insertion order for LRU eviction
    
    searchInputs.forEach(function(input) {
      const container = input.closest('.search-container');
      const dropdown = container.querySelector('.search-results-dropdown');
      const dropdownId = dropdown ? dropdown.id : '';
      let currentHighlight = -1;
      let searchTimeout;
      let searchCache = {};
      
      // Add to cache with size limit (LRU eviction)
      const addToCache = function(key, value) {
        // If key already exists, remove it from order tracking (will be re-added at end)
        const existingIndex = searchCacheKeys.indexOf(key);
        if (existingIndex > -1) {
          searchCacheKeys.splice(existingIndex, 1);
        }
        
        // Evict oldest entry if cache is full
        if (searchCacheKeys.length >= MAX_CACHE_SIZE) {
          const oldestKey = searchCacheKeys.shift();
          delete searchCache[oldestKey];
        }
        
        // Add new entry
        searchCache[key] = value;
        searchCacheKeys.push(key);
      };
      
      // Move dropdown to body so it can stack above header/nav
      if (dropdown && dropdown.parentElement !== document.body) {
        dropdown.classList.add('search-results-portal');
        document.body.appendChild(dropdown);
      }
      
      const positionDropdown = function() {
        const rect = container.getBoundingClientRect();
        const margin = 4; // small gap below the input
        dropdown.style.position = 'fixed';
        dropdown.style.top = `${rect.bottom + margin}px`;
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.width = `${rect.width}px`;
        dropdown.style.zIndex = '2000';
      };
      
      // Debounced search function
      const performSearch = function(query) {
        if (query.length < 2) {
          hideDropdown();
          return;
        }
        
        // Check cache first
        if (searchCache[query]) {
          displayResults(searchCache[query]);
          return;
        }
        
        // Show loading state
        dropdown.innerHTML = '<div class="search-loading">' + searchingLabel + '</div>';
        showDropdown();
        
        // Use a simpler approach with Shopify's search endpoint
        const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6`;
        
        fetch(searchUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            addToCache(query, data);
            displayResults(data);
          })
          .catch(error => {
            console.error('Search error:', error);
            
            // Fallback: try the regular search page approach
            const fallbackUrl = `/search?q=${encodeURIComponent(query)}&type=product,collection&view=json`;
            
            fetch(fallbackUrl)
              .then(response => response.json())
              .then(data => {
                addToCache(query, data);
                displayResults(data);
              })
              .catch(fallbackError => {
                console.error('Fallback search error:', fallbackError);
                dropdown.innerHTML = `<div class="search-no-results">${searchUnavailableLabel}</div>`;
              });
          });
      };
      
      const displayResults = function(data) {
        let html = '';
        let optionIndex = 0;
        
        // Handle different response formats
        let products = [];
        let collections = [];
        
        // Check for suggest.json format
        if (data.resources && data.resources.results) {
          products = data.resources.results.products || [];
          collections = data.resources.results.collections || [];
        }
        // Check for direct results format
        else if (data.products || data.collections) {
          products = data.products || [];
          collections = data.collections || [];
        }
        // Check for simple array format
        else if (Array.isArray(data)) {
          // Separate products and collections
          data.forEach(function(item) {
            if (item.type === 'product' || item.price !== undefined) {
              products.push(item);
            } else {
              collections.push(item);
            }
          });
        }
        
        // Display products
        if (products && products.length > 0) {
          html += '<div class="search-results-section">';
          html += '<div class="search-results-title">' + productsLabel + '</div>';
          
          products.forEach(function(product) {
            const image = product.featured_image ? product.featured_image.url || product.featured_image : '';
            
            // Format price properly
            let price = '';
            if (product.price) {
              let priceValue = parseFloat(product.price).toFixed(2);
              // Only replace the first occurrence to avoid double replacement
              price = moneyFormat.replace(/\{\{\s*amount\s*\}\}/, priceValue);
            }
            
            const url = product.url || `/products/${escapeHtml(product.handle)}`;
            
            // Escape all user-controlled data to prevent XSS
            const safeUrl = escapeHtml(url);
            const safeTitle = escapeHtml(product.title);
            const safeImage = escapeHtml(image);
            
            const optionId = `${input.id}-search-option-${optionIndex++}`;
            html += `<a href="${safeUrl}" class="search-result-item" data-url="${safeUrl}" role="option" aria-selected="false" id="${optionId}" tabindex="-1">`;
            if (image) {
              html += `<img src="${safeImage}" alt="${safeTitle}" class="search-result-image">`;
            } else {
              html += '<div class="search-result-image bg-gray-200"></div>';
            }
            html += '<div class="search-result-content">';
            html += `<div class="search-result-title">${safeTitle}</div>`;
            if (price) {
              html += `<div class="search-result-price"><span class="text-xs text-sage">${fromPriceLabel}</span> ${price}</div>`;
            }
            html += '</div>';
            html += '</a>';
          });
          
          html += '</div>';
        }
        
        // Display collections
        if (collections && collections.length > 0) {
          html += '<div class="search-results-section">';
          html += '<div class="search-results-title">' + collectionsLabel + '</div>';
          
          collections.forEach(function(collection) {
            const image = collection.featured_image ? collection.featured_image.url || collection.featured_image : '';
            const url = collection.url || `/collections/${escapeHtml(collection.handle)}`;
            
            // Escape all user-controlled data to prevent XSS
            const safeUrl = escapeHtml(url);
            const safeTitle = escapeHtml(collection.title);
            const safeImage = escapeHtml(image);
            
            const optionId = `${input.id}-search-option-${optionIndex++}`;
            html += `<a href="${safeUrl}" class="search-result-item" data-url="${safeUrl}" role="option" aria-selected="false" id="${optionId}" tabindex="-1">`;
            if (image) {
              html += `<img src="${safeImage}" alt="${safeTitle}" class="search-result-image">`;
            } else {
              html += '<div class="search-result-image bg-gray-200"></div>';
            }
            html += '<div class="search-result-content">';
            html += `<div class="search-result-title">${safeTitle}</div>`;
            html += '<div class="search-result-type">' + collectionTypeLabel + '</div>';
            html += '</div>';
            html += '</a>';
          });
          
          html += '</div>';
        }
        
        if (!html) {
          html = '<div class="search-no-results">' + noResultsLabel + '</div>';
        }
        
        dropdown.innerHTML = html;
        showDropdown();
        currentHighlight = -1;
      };
      
      const showDropdown = function() {
        positionDropdown();
        dropdown.classList.remove('hidden');
        input.setAttribute('aria-expanded', 'true');
      };
      
      const hideDropdown = function() {
        dropdown.classList.add('hidden');
        currentHighlight = -1;
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-activedescendant', '');
      };
      
      const highlightItem = function(index) {
        const items = dropdown.querySelectorAll('.search-result-item');
        
        // Remove previous highlight
        items.forEach(item => {
          item.classList.remove('highlighted');
          item.setAttribute('aria-selected', 'false');
        });
        
        // Add new highlight
        if (index >= 0 && index < items.length) {
          items[index].classList.add('highlighted');
          items[index].setAttribute('aria-selected', 'true');
          currentHighlight = index;
          input.setAttribute('aria-activedescendant', items[index].id || '');
        } else {
          currentHighlight = -1;
          input.setAttribute('aria-activedescendant', '');
        }
      };
      
      // Event listeners
      input.addEventListener('input', function(e) {
        const query = e.target.value.trim();
        
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(function() {
          performSearch(query);
        }, 300);
      });
      
      input.addEventListener('keydown', function(e) {
        const items = dropdown.querySelectorAll('.search-result-item');
        
        switch(e.key) {
          case 'ArrowDown':
            e.preventDefault();
            const nextIndex = currentHighlight < items.length - 1 ? currentHighlight + 1 : 0;
            highlightItem(nextIndex);
            break;
            
          case 'ArrowUp':
            e.preventDefault();
            const prevIndex = currentHighlight > 0 ? currentHighlight - 1 : items.length - 1;
            highlightItem(prevIndex);
            break;
            
          case 'Enter':
            if (currentHighlight >= 0 && items[currentHighlight]) {
              e.preventDefault();
              const url = items[currentHighlight].getAttribute('data-url');
              if (url) {
                window.location.href = url;
              }
            }
            break;
            
          case 'Escape':
            hideDropdown();
            input.blur();
            break;
        }
      });
      
      input.addEventListener('focus', function() {
        if (input.value.trim().length >= 2) {
          performSearch(input.value.trim());
        }
      });
      
      // Click outside to close
      document.addEventListener('click', function(e) {
        if (!container.contains(e.target) && !dropdown.contains(e.target)) {
          hideDropdown();
        }
      });

      window.addEventListener('resize', function() {
        if (!dropdown.classList.contains('hidden')) {
          positionDropdown();
        }
      });
      window.addEventListener('scroll', function() {
        if (!dropdown.classList.contains('hidden')) {
          positionDropdown();
        }
      }, true);
      
      // Handle clicks on search results
      dropdown.addEventListener('click', function(e) {
        const item = e.target.closest('.search-result-item');
        if (item) {
          const url = item.getAttribute('data-url');
          if (url) {
            window.location.href = url;
          }
        }
      });

      if (dropdownId) {
        input.setAttribute('aria-controls', dropdownId);
      }
    });
  };

  const initCustomerDropdown = function() {
    const dropdownButton = document.querySelector('[aria-controls="customer-dropdown"]');
    const dropdown = document.getElementById('customer-dropdown');
    
    if (dropdownButton && dropdown) {
      dropdownButton.addEventListener('click', function(e) {
        e.stopPropagation();
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', !isExpanded);
        dropdown.classList.toggle('hidden');
      });
      
      // Close dropdown when clicking outside
      document.addEventListener('click', function(e) {
        if (!dropdownButton.contains(e.target) && !dropdown.contains(e.target)) {
          dropdownButton.setAttribute('aria-expanded', 'false');
          dropdown.classList.add('hidden');
        }
      });
    }
  };
  
  const initNavDropdowns = function() {
    const dropdownButtons = document.querySelectorAll('.nav-dropdown-toggle');
    
    dropdownButtons.forEach(function(button) {
      const submenuId = button.getAttribute('aria-controls');
      const submenu = document.getElementById(submenuId);
      
      if (submenu) {
        button.addEventListener('click', function(e) {
          e.stopPropagation();
          const isExpanded = this.getAttribute('aria-expanded') === 'true';
          
          // Close all other dropdowns
          dropdownButtons.forEach(function(otherButton) {
            if (otherButton !== button) {
              otherButton.setAttribute('aria-expanded', 'false');
              const otherSubmenuId = otherButton.getAttribute('aria-controls');
              const otherSubmenu = document.getElementById(otherSubmenuId);
              if (otherSubmenu) otherSubmenu.classList.add('hidden');
            }
          });
          
          // Toggle current dropdown
          this.setAttribute('aria-expanded', !isExpanded);
          submenu.classList.toggle('hidden');
        });
      }
    });
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', function(e) {
      dropdownButtons.forEach(function(button) {
        const submenuId = button.getAttribute('aria-controls');
        const submenu = document.getElementById(submenuId);
        
        if (submenu && !button.contains(e.target) && !submenu.contains(e.target)) {
          button.setAttribute('aria-expanded', 'false');
          submenu.classList.add('hidden');
        }
      });
    });
  };
  
  // Initialize cart icon click handler
  const initCartIcon = function() {
    const cartIcon = document.querySelector('.cart-icon');
    if (cartIcon) {
      cartIcon.addEventListener('click', function(e) {
        // Only prevent default if cart drawer is available
        if (window.cartDrawer && window.cartDrawer.open) {
          e.preventDefault();
          window.cartDrawer.open();
        }
        // Otherwise, let the link work normally (fallback to cart page)
      });
    }
  };
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initSearchTypeahead();
      initCustomerDropdown();
      initNavDropdowns();
      initCartIcon();
    });
  } else {
    initSearchTypeahead();
    initCustomerDropdown();
    initNavDropdowns();
    initCartIcon();
  }
})();
